import AddRounded from '@mui/icons-material/AddRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import LocalOfferRounded from '@mui/icons-material/LocalOfferRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import {
  Box,
  ButtonBase,
  IconButton,
  InputBase,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Money } from '../../../components/Money';
import { productQuery, unitOptions } from '../lib/api';
import { switchUnit, type CartAction, type CartLine } from '../lib/cart';
import { dec, toPlain } from '../lib/decimal';
import type { LineTotals } from '../lib/totals';
import { ItemTile } from './bits';

/** Quantity stepper: − [qty] + with a typeable middle. */
function Stepper({ line, dispatch }: { line: CartLine; dispatch: (a: CartAction) => void }) {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: '10px',
        bgcolor: 'background.paper',
        height: 36,
      }}
    >
      <IconButton
        size="small"
        aria-label={`One less ${line.name}`}
        onClick={() => dispatch({ type: 'step', key: line.key, by: -1 })}
        sx={{ borderRadius: '9px', width: 34, height: 34 }}
      >
        <RemoveRounded fontSize="small" />
      </IconButton>
      <InputBase
        value={line.qty}
        onChange={(e) =>
          dispatch({ type: 'setQty', key: line.key, qty: e.target.value.replace(/[^\d.]/g, '') })
        }
        onFocus={(e) => e.target.select()}
        inputProps={{
          'aria-label': `Quantity of ${line.name}`,
          inputMode: 'decimal',
          className: 'tabular',
          style: { textAlign: 'center', padding: 0, fontWeight: 650 },
        }}
        sx={{ width: 44 }}
      />
      <IconButton
        size="small"
        aria-label={`One more ${line.name}`}
        onClick={() => dispatch({ type: 'step', key: line.key, by: 1 })}
        sx={{ borderRadius: '9px', width: 34, height: 34 }}
      >
        <AddRounded fontSize="small" />
      </IconButton>
    </Stack>
  );
}

/** Sell as: the line's unit (piece, box, carton…), shown when the item has pack sizes. */
function UnitSelect({ line, dispatch }: { line: CartLine; dispatch: (a: CartAction) => void }) {
  const product = useQuery({ ...productQuery(line.productId), enabled: !line.combo?.length });
  const units = product.data ? unitOptions(product.data) : [];
  if (units.length <= 1) return null;
  const base = units[0]?.symbol ?? '';
  return (
    <TextField
      select
      size="small"
      value={line.unitId}
      aria-label={`Unit of ${line.name}`}
      onChange={(e) => {
        const u = units.find((x) => x.unitId === e.target.value);
        if (u) dispatch({ type: 'update', key: line.key, patch: switchUnit(line, u) });
      }}
      slotProps={{
        htmlInput: { 'aria-label': `Unit of ${line.name}` },
        input: { sx: { height: 30, fontSize: '0.8125rem', fontWeight: 650 } },
      }}
      sx={{ mt: 0.75, width: '100%' }}
    >
      {units.map((u) => (
        <MenuItem key={u.unitId} value={u.unitId}>
          {u.symbol}
          {u.factor !== '1' ? ` (${toPlain(dec(u.factor))} ${base})` : ''}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** The counter cart: one row per item, newest at the bottom, click a row to edit it. */
export function CartLines({
  lines,
  totals,
  highlight,
  dispatch,
  onEdit,
}: {
  lines: CartLine[];
  totals: LineTotals[];
  /** `variantId:unitId` of the line to flash. */
  highlight: string | null;
  dispatch: (a: CartAction) => void;
  onEdit: (key: string) => void;
}) {
  return (
    <Box role="list" aria-label="Items in this sale">
      {lines.map((l, i) => {
        const t = totals[i];
        const discounted = dec(l.unitPrice) < dec(l.listPrice) || (t && dec(t.lineDiscount) > 0n);
        return (
          <Box
            role="listitem"
            key={l.key}
            sx={(th) => ({
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr auto',
                sm: 'minmax(0,1fr) 128px 112px 120px 36px',
              },
              gridTemplateAreas: {
                xs: '"item item" "qty total"',
                sm: '"item qty price total del"',
              },
              alignItems: 'center',
              columnGap: 2,
              rowGap: 1.25,
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 0 },
              transition: 'background-color 600ms',
              bgcolor:
                highlight === `${l.variantId}:${l.unitId}`
                  ? `color-mix(in srgb, ${(th.vars ?? th).palette.primary.main} 8%, transparent)`
                  : 'transparent',
              '&:hover .sm-del': { opacity: 1 },
            })}
          >
            <ButtonBase
              onClick={() => onEdit(l.key)}
              sx={{
                gridArea: 'item',
                justifyContent: 'flex-start',
                textAlign: 'left',
                gap: 1.5,
                borderRadius: '10px',
                minWidth: 0,
                p: 0.25,
              }}
            >
              <ItemTile name={l.name} size={42} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.3,
                  }}
                >
                  {l.name}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {l.combo?.length ? `${l.combo.map((p) => p.name).join(' · ')} · ` : ''}
                    {l.sku}
                    {l.factor !== '1' ? ` · ${l.unitSymbol}` : ''}
                    <Box component="span" sx={{ display: { sm: 'none' } }}>
                      {' · '}
                      <Money value={l.unitPrice} /> / {l.unitSymbol}
                    </Box>
                  </Typography>
                  {discounted && (
                    <LocalOfferRounded
                      sx={{ fontSize: 14, color: 'success.main' }}
                      aria-label="Discounted"
                    />
                  )}
                </Stack>
              </Box>
            </ButtonBase>
            <Box sx={{ gridArea: 'qty' }}>
              <Stepper line={l} dispatch={dispatch} />
              <UnitSelect line={l} dispatch={dispatch} />
            </Box>
            <Box
              sx={{ gridArea: 'price', textAlign: 'right', display: { xs: 'none', sm: 'block' } }}
            >
              <Typography className="tabular" variant="body2">
                <Money value={l.unitPrice} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                per {l.unitSymbol}
              </Typography>
            </Box>
            <Box sx={{ gridArea: 'total', textAlign: 'right' }}>
              <Typography className="tabular" sx={{ fontWeight: 650 }}>
                <Money value={t?.total ?? '0'} />
              </Typography>
              {t && dec(t.lineDiscount) > 0n && (
                <Typography variant="caption" color="success.main" className="tabular">
                  −<Money value={t.lineDiscount} />
                </Typography>
              )}
            </Box>
            <Tooltip title="Remove">
              <IconButton
                className="sm-del"
                size="small"
                aria-label={`Remove ${l.name}`}
                onClick={() => dispatch({ type: 'remove', key: l.key })}
                sx={{
                  gridArea: 'del',
                  display: { xs: 'none', sm: 'inline-flex' },
                  opacity: { sm: 0.35 },
                  transition: 'opacity 140ms',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <CloseRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}
